import { useUserInfo } from '@/provider/UserInfoProvider';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useInterval } from 'react-use';
import { useSignupModal } from '@/zustand/store';
import { useCallback, useEffect, useState } from 'react';
import { RPostHog, posthogUTM } from '@/posthog';
import { kazeFetch } from '@/utils/kazeFetch';
import { generateInviteCodeEndpoint } from '@/config/endpoints';

const { utm } = posthogUTM();

const generateInviteCode = () =>
  kazeFetch(generateInviteCodeEndpoint, { method: 'POST' });

const useInvite = ({
  onSuccess,
  isCopyUrl = true,
  isAutoMutate = false,
}: {
  onSuccess?: (data: any) => void;
  isCopyUrl?: boolean;
  isAutoMutate?: boolean;
} = {}) => {
  const setSignupModalOpen = useSignupModal((s) => s.setSignupModalOpen);
  const [shouldMutate, setShouldMutate] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [inviteUrl, setInviteUrl] = useState('');
  const [pendingReportData, setPendingReportData] = useState<{
    invite_code: string;
    generate_link: string;
  } | null>(null);
  const { userIdentity, userInfo, isLoadingUser } = useUserInfo();

  const inviteReport = useCallback(
    (invite_code?: string, generate_link?: string) => {
      RPostHog('invite_link_generated', {
        user_id: userInfo?.user_id || 'not_get_user',
        email: userInfo?.email || 'not_get_email',
        user_identity: userIdentity,
        invite_code:
          pendingReportData?.invite_code || invite_code || 'no_get_code',
        generate_link:
          pendingReportData?.generate_link || generate_link || 'no_get_link',
        ...utm,
      });
    },
    [userInfo, userIdentity, pendingReportData]
  );

  // 监听 userInfo 变化，如果有待上报数据且 userInfo 已加载，则进行上报
  useEffect(() => {
    if (
      userInfo &&
      userInfo.is_anonymous === false &&
      pendingReportData &&
      !isLoadingUser
    ) {
      inviteReport();
      // 清除待上报数据
      setPendingReportData(null);
    }
  }, [userInfo, pendingReportData, isLoadingUser, inviteReport]);

  const { mutate, isPending } = useMutation({
    mutationFn: (_params: { isSilent?: boolean } = {}) => generateInviteCode(),
    onSuccess: (data, variables) => {
      onSuccess?.(data);
      const url = `${window.location.origin}/invite?invite_code=${data!.invite_code}&utm_source=referal&utm_medium=referal&utm_campaign=referal&utm_content=${data!.invite_code}`;
      setInviteUrl(url);
      if (variables?.isSilent) return;
      if (isCopyUrl) {
        setTimeLeft(3);
        setTimeout(() => {
          navigator.clipboard
            .writeText(url)
            .then(() => {
              toast.success('URL copied to clipboard');
            })
            .catch(() => {
              toast.error('Failed to copy URL');
            });
        });
      }
      // 如果 userInfo 已经有值，直接上报；否则存储待上报数据
      if (userInfo && userInfo.is_anonymous === false && !isLoadingUser) {
        inviteReport(data?.invite_code, url);
      } else if (data?.invite_code) {
        // 存储待上报数据，等待 userInfo 加载完成
        setPendingReportData({
          invite_code: data.invite_code,
          generate_link: url,
        });
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  useInterval(
    () => {
      if (timeLeft > 0) {
        setTimeLeft(timeLeft - 1);
      }
    },
    timeLeft > 0 ? 1000 : null
  );

  const handleInvite = () => {
    userIdentity !== 3
      ? setSignupModalOpen(true, {
          signupSuccess: () => setShouldMutate(true),
          description: 'Sign up to get your invite link!',
        })
      : mutate({});
  };

  useEffect(() => {
    if (!isLoadingUser && userIdentity === 3 && shouldMutate) {
      mutate({});
      setShouldMutate(false);
    }
  }, [isLoadingUser, userIdentity, mutate, shouldMutate, setShouldMutate]);

  useEffect(() => {
    if (isAutoMutate && !isLoadingUser && userIdentity === 3) {
      mutate({ isSilent: true });
    }
  }, [isAutoMutate, isLoadingUser, mutate, userIdentity]);

  return {
    handleInvite,
    inviteIsPending: isPending,
    inviteTimeLeft: timeLeft,
    inviteUrl,
  };
};

export default useInvite;
